/**
 * MathLayout — mirrors AppLayout exactly, but:
 * - Fetches only sessions with category='math'
 * - Uses MathChatPanel (with KaTeX + math agent endpoints) instead of V7AgentChatPanel
 * - Passes basePath="/math" to SessionNavigator
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SessionNavigator from '../session-navigator/SessionNavigator';
import ContentArea from '../content-area/ContentArea';
import MathChatPanel from './MathChatPanel';
import styles from './MathLayout.module.css';

const MathLayout = () => {
    const { deviceId } = useParams();
    const navigate = useNavigate();

    const [sessions, setSessions] = useState([]);
    const [sessionDevices, setSessionDevices] = useState({});
    const [navigatorCollapsed, setNavigatorCollapsed] = useState(false);
    const [transcriptOpen, setTranscriptOpen] = useState(false);

    // Mutual exclusion: opening transcript collapses navigator; expanding navigator closes transcript
    const handleTranscriptToggle = (open) => {
        setTranscriptOpen(open);
        if (open) {
            setNavigatorCollapsed(true);
        } else {
            setNavigatorCollapsed(false);
        }
    };

    const handleNavigatorCollapse = (collapsed) => {
        setNavigatorCollapsed(collapsed);
        if (!collapsed) setTranscriptOpen(false);
    };

    useEffect(() => {
        fetchSessionsAndDevices();
    }, []);

    const fetchSessionsAndDevices = async () => {
        try {
            const res = await fetch('/api/v1/sessions?category=math');
            if (!res.ok) return;
            const sessionsData = await res.json();
            setSessions(sessionsData);

            const devicesMap = {};
            await Promise.all(sessionsData.map(async (s) => {
                try {
                    const devRes = await fetch(`/api/v1/sessions/${s.id}/devices`);
                    devicesMap[s.id] = devRes.ok ? await devRes.json() : [];
                } catch (_) {
                    devicesMap[s.id] = [];
                }
            }));
            setSessionDevices(devicesMap);

            // Auto-select first device if none selected
            if (!deviceId && sessionsData.length > 0) {
                const firstSession = sessionsData[0];
                const firstDevices = devicesMap[firstSession.id] || [];
                if (firstDevices.length > 0) {
                    navigate(`/math/${firstDevices[0].id}/assessment`, { replace: true });
                }
            }
        } catch (_) {}
    };

    const activeDeviceInfo = useMemo(() => {
        if (!deviceId) return null;
        for (const [sessionId, devices] of Object.entries(sessionDevices)) {
            const device = devices.find(d => String(d.id) === String(deviceId));
            if (device) {
                const session = sessions.find(s => String(s.id) === String(sessionId));
                return { device, session, sessionId: parseInt(sessionId) };
            }
        }
        return null;
    }, [deviceId, sessionDevices, sessions]);

    return (
        <div className={styles.layout}>
            <div className={styles.leftPanel}>
                <SessionNavigator
                    sessions={sessions}
                    sessionDevices={sessionDevices}
                    activeDeviceId={deviceId}
                    collapsed={navigatorCollapsed}
                    onCollapsedChange={handleNavigatorCollapse}
                    basePath="/math"
                    onImportSession={fetchSessionsAndDevices}
                />
                <ContentArea
                    deviceId={deviceId}
                    deviceInfo={activeDeviceInfo}
                    transcriptOpen={transcriptOpen}
                    onTranscriptToggle={handleTranscriptToggle}
                />
            </div>
            <div className={styles.rightPanel}>
                <MathChatPanel
                    sessionDeviceId={deviceId ? parseInt(deviceId) : null}
                />
            </div>
        </div>
    );
};

export default MathLayout;
